import { apiFetchAuthed, getAccessToken } from '../apiHelpers';
import config from '../config';
import useUserStore from '../stores/useUserStore';
import type {
  ActiveUser,
  ChatTurn,
  MemberRole,
  PlanEdge,
  PlanExport,
  PlanMember,
  NewNode,
  NodePatch,
  PlanDetail,
  PlanNode,
  PlanPatch,
  PlanResponse,
  PlanSummary,
  TopPoint,
} from './types';

interface Versioned {
  version: number;
}

// The same exit `useApi` wires in: a session that cannot be refreshed signs the user out.
const onUnauthorized = () => useUserStore.getState().logout();

const call = (url: string, options: Record<string, unknown> = {}) => apiFetchAuthed(url, { onUnauthorized, ...options });

const base = (planId: string) => `/node-plans/${encodeURIComponent(planId)}`;
const nodePath = (planId: string, nodeId: string) => `${base(planId)}/nodes/${encodeURIComponent(nodeId)}`;

export const listPlans = (archived = false): Promise<{ items: PlanSummary[] }> =>
  call(`/node-plans${archived ? '?archived=1' : ''}`);

export const createPlan = (body: { title: string; description?: string }): Promise<{ plan: PlanSummary }> =>
  call('/node-plans', { method: 'POST', body });

// Silent: a 403 here is the no-access page, not a console error.
export const getPlan = (planId: string, { done }: { done?: boolean } = {}): Promise<PlanResponse> =>
  call(`${base(planId)}${done ? '?done=1' : ''}`, { silent: true });

export const updatePlan = (planId: string, patch: PlanPatch): Promise<{ plan: PlanDetail }> =>
  call(base(planId), { method: 'PATCH', body: patch });

export const createNode = (planId: string, body: NewNode): Promise<{ node: PlanNode } & Versioned> =>
  call(`${base(planId)}/nodes`, { method: 'POST', body });

export const updateNode = (planId: string, nodeId: string, patch: NodePatch): Promise<{ node: PlanNode } & Versioned> =>
  call(nodePath(planId, nodeId), { method: 'PATCH', body: patch });

export const acceptNode = (planId: string, nodeId: string): Promise<{ node: PlanNode } & Versioned> =>
  call(`${nodePath(planId, nodeId)}/accept`, { method: 'POST' });

export const rejectNode = (planId: string, nodeId: string): Promise<Versioned> =>
  call(`${nodePath(planId, nodeId)}/reject`, { method: 'POST' });

export const deleteNode = (planId: string, nodeId: string): Promise<Versioned> =>
  call(nodePath(planId, nodeId), { method: 'DELETE' });

export const reorderNodes = (planId: string, ids: string[]): Promise<Versioned> =>
  call(`${base(planId)}/nodes/order`, { method: 'PUT', body: { ids } });

const edgesPath = (planId: string) => `${base(planId)}/edges`;

export const createEdge = (
  planId: string,
  body: { source_id: string; target_id: string; label?: string },
): Promise<{ edge: PlanEdge } & Versioned> =>
  call(edgesPath(planId), { method: 'POST', body });

/** An empty label clears it; the server trims and allows up to 200 characters. */
export const updateEdge = (planId: string, edgeId: string, patch: { label: string }): Promise<{ edge: PlanEdge } & Versioned> =>
  call(`${edgesPath(planId)}/${encodeURIComponent(edgeId)}`, { method: 'PATCH', body: patch });

export const deleteEdge = (planId: string, edgeId: string): Promise<Versioned> =>
  call(`${edgesPath(planId)}/${encodeURIComponent(edgeId)}`, { method: 'DELETE' });

// ── Sharing ──

export const listActiveUsers = (): Promise<ActiveUser[]> => call('/users/active', { silent: true });

export const addMember = (planId: string, userId: string, role: MemberRole = 'member'): Promise<{ member: PlanMember } & Versioned> =>
  call(`${base(planId)}/members`, { method: 'POST', body: { user_id: userId, role } });

export const setMemberRole = (planId: string, userId: string, role: MemberRole): Promise<{ member: PlanMember } & Versioned> =>
  call(`${base(planId)}/members/${encodeURIComponent(userId)}`, { method: 'PATCH', body: { role } });

export const removeMember = (planId: string, userId: string): Promise<Versioned> =>
  call(`${base(planId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });

export const transferPlan = (planId: string, userId: string): Promise<{ plan: PlanDetail } & Versioned> =>
  call(`${base(planId)}/transfer`, { method: 'POST', body: { user_id: userId } });

// Silent: 429 and 409 are answers the no-access page shows, not console errors.
export const requestAccess = (planId: string): Promise<{ ok: true }> =>
  call('/node-plans/access-requests', { method: 'POST', body: { plan_id: planId }, silent: true });

// ── Reading the plan whole ──

export const getTopPoints = (planId: string, limit = 20): Promise<{ points: TopPoint[] }> =>
  call(`${base(planId)}/points/top?limit=${limit}`);

export const getExport = (planId: string, { done }: { done?: boolean } = {}): Promise<PlanExport> =>
  call(`${base(planId)}/export.json${done ? '?done=1' : ''}`);

// ── Chat ──

export const getChatStatus = (planId: string): Promise<{ available: boolean }> =>
  call(`${base(planId)}/chat/status`, { silent: true });

export interface ChatStreamError extends Error {
  status?: number;
}

const streamError = (message: string, status?: number): ChatStreamError => {
  const err: ChatStreamError = new Error(message);
  err.status = status;
  return err;
};

const openChatStream = (planId: string, body: { turns: ChatTurn[]; plan_version: number }, signal: AbortSignal) => {
  const token = getAccessToken();
  return fetch(`${config.API_ENDPOINT}${base(planId)}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
};

/**
 * POST the conversation and read the answer as it streams. `onToken` gets each piece
 * of text; resolves with the plan version the answer was written against. Throws a
 * `ChatStreamError` carrying the HTTP status (429, 503) or the stream's own message.
 *
 * fetch rather than EventSource: EventSource cannot POST or send the Bearer header.
 */
export async function streamPlanChat(
  planId: string,
  body: { turns: ChatTurn[]; plan_version: number },
  onToken: (text: string) => void,
  signal: AbortSignal,
): Promise<{ version: number | null }> {
  let res = await openChatStream(planId, body, signal);
  if (res.status === 401) {
    // An expired token: any call through apiFetchAuthed refreshes it (or signs the
    // user out when it cannot), then the stream gets one more try.
    await getChatStatus(planId).catch(() => undefined);
    res = await openChatStream(planId, body, signal);
  }
  if (!res.ok) {
    let message = '';
    try {
      message = JSON.parse(await res.text())?.error?.message || '';
    } catch {
      /* not JSON */
    }
    throw streamError(message || `The assistant could not answer (${res.status}).`, res.status);
  }
  if (!res.body) throw streamError('The assistant could not answer.');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let partial = '';
  let version: number | null = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    partial += decoder.decode(value, { stream: true });
    const events = partial.split('\n\n');
    partial = events.pop() || '';
    for (const raw of events) {
      let type = '';
      let data = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) type = line.slice(7).trim();
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (!data) continue;
      let parsed: { text?: string; version?: number; message?: string };
      try {
        parsed = JSON.parse(data);
      } catch {
        continue;
      }
      if (type === 'token' && parsed.text) onToken(parsed.text);
      else if (type === 'done') version = typeof parsed.version === 'number' ? parsed.version : null;
      else if (type === 'error') throw streamError(parsed.message || 'The assistant stopped partway. Try again.');
    }
  }
  return { version };
}

/** The server's message when it sent one, else a plain fallback. */
export const errorMessage = (e: unknown, fallback = 'Something went wrong. Try again.'): string => {
  const err = e as { data?: { error?: { message?: string } }; message?: string } | null;
  return err?.data?.error?.message || err?.message || fallback;
};
